import { useEffect, useState } from 'react'
import { useAppStore } from '../../store/app'
import type { ChatNotifMode } from '../../store/types'
import { fetchChatNotifPrefs, setChatNotifPrefs, setChatRoomHost } from '../../api/ws'

const MODES: { mode: ChatNotifMode; icon: string; label: string; description: string }[] = [
  {
    mode: 'all',
    icon: '🔔',
    label: 'All messages',
    description: 'Push notification for every new message.',
  },
  {
    mode: 'mentions',
    icon: '@',
    label: 'Mentions only',
    description: 'Push only when someone @-mentions you.',
  },
  {
    mode: 'muted',
    icon: '🔇',
    label: 'Muted',
    description: 'No push notifications from this room.',
  },
]

export function ChatSettingsSheet({
  roomID,
  roomName,
}: {
  roomID: string
  roomName: string
}) {
  const setChatNotifMode = useAppStore((s) => s.setChatNotifMode)
  const cached = useAppStore((s) => s.chatNotifModes[roomID])
  const room = useAppStore((s) => s.chatRooms.find((r) => r.id === roomID))
  const hosts = useAppStore((s) => s.hosts)
  const wdPath = room?.working_directory_path

  const [mode, setMode] = useState<ChatNotifMode>(cached ?? 'all')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savingHost, setSavingHost] = useState(false)
  const [hostError, setHostError] = useState<string | null>(null)

  const isOwner = room?.role === 'owner'
  // hosts in the store are the household's live (non-revoked) hosts. A room's
  // stored host_id that isn't among them was removed → the wd is stale.
  const boundHost = hosts.find((h) => h.host_id === room?.host_id)
  const hostRemoved = !!room?.host_id && !boundHost

  async function rebindHost(nextHostID: string) {
    if (savingHost || nextHostID === room?.host_id) return
    setSavingHost(true)
    setHostError(null)
    try {
      const r = await setChatRoomHost(roomID, nextHostID)
      // On success the server pushes a fresh chat_rooms_list, so host_id and the
      // derived working_directory_path refresh from the store — nothing to set here.
      if (r.error) setHostError(r.error)
    } catch (e) {
      setHostError(e instanceof Error ? e.message : String(e))
    } finally {
      setSavingHost(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    fetchChatNotifPrefs(roomID)
      .then((r) => {
        if (cancelled) return
        if (r.error) { setError(r.error); return }
        const m = r.mode as ChatNotifMode
        if (m === 'all' || m === 'mentions' || m === 'muted') {
          setMode(m)
          setChatNotifMode(roomID, m)
        }
      })
      .catch((e: Error) => { if (!cancelled) setError(e.message) })
    return () => { cancelled = true }
  }, [roomID]) // eslint-disable-line react-hooks/exhaustive-deps

  async function pick(next: ChatNotifMode) {
    if (next === mode || busy) return
    const prev = mode
    setMode(next)
    setChatNotifMode(roomID, next)
    setBusy(true)
    setError(null)
    try {
      const r = await setChatNotifPrefs(roomID, next)
      if (r.error) {
        setMode(prev)
        setChatNotifMode(roomID, prev)
        setError(r.error)
      }
    } catch (e) {
      setMode(prev)
      setChatNotifMode(roomID, prev)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: '4px 0 16px' }}>
      {error && (
        <div style={{ margin: '0 16px 12px', padding: '8px 12px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
          {error}
        </div>
      )}

      <div style={{ padding: '0 16px 8px', fontSize: 12, fontWeight: 600, color: 'var(--label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Notifications · {roomName}
      </div>

      <div style={{ padding: '0 16px 6px', fontSize: 13, color: 'var(--label-secondary)' }}>
        Applies to all your devices.
      </div>

      <div style={{ background: 'var(--bg-grouped-cell)', borderRadius: 12, margin: '0 16px', overflow: 'hidden', opacity: busy ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        {MODES.map((row, i) => {
          const selected = mode === row.mode
          return (
            <button
              key={row.mode}
              type="button"
              onClick={() => void pick(row.mode)}
              disabled={busy}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                padding: '12px 16px',
                background: 'transparent',
                borderTop: i > 0 ? '0.5px solid var(--separator)' : 'none',
                textAlign: 'left',
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              <span style={{ fontSize: 20, width: 28, textAlign: 'center', flexShrink: 0 }}>
                {row.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: selected ? 600 : 400, color: 'var(--label)' }}>
                  {row.label}
                </div>
                <div style={{ fontSize: 13, color: 'var(--label-secondary)', marginTop: 1 }}>
                  {row.description}
                </div>
              </div>
              {selected && (
                <span style={{ color: 'var(--tint)', fontSize: 18, fontWeight: 700, flexShrink: 0 }}>✓</span>
              )}
            </button>
          )
        })}
      </div>

      <div style={{ padding: '16px 16px 0', fontSize: 12, fontWeight: 600, color: 'var(--label-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Files
      </div>
      <div style={{ padding: '6px 16px 0', fontSize: 13, color: 'var(--label-secondary)' }}>
        Attachments in this room are saved on the host below.
      </div>

      {hostError && (
        <div style={{ margin: '8px 16px 0', padding: '8px 12px', background: 'color-mix(in srgb, var(--danger) 12%, transparent)', borderRadius: 8, fontSize: 13, color: 'var(--danger)' }}>
          {hostError}
        </div>
      )}

      {isOwner && hosts.length > 0 ? (
        <div style={{ background: 'var(--bg-grouped-cell)', borderRadius: 12, margin: '8px 16px 0', overflow: 'hidden', opacity: savingHost ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          {hosts.map((h, i) => {
            const selected = h.host_id === room?.host_id
            return (
              <button
                key={h.host_id}
                type="button"
                onClick={() => void rebindHost(h.host_id)}
                disabled={savingHost}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '12px 16px',
                  background: 'transparent',
                  borderTop: i > 0 ? '0.5px solid var(--separator)' : 'none',
                  textAlign: 'left',
                  cursor: savingHost ? 'default' : 'pointer',
                }}
              >
                <span style={{ fontSize: 18, width: 28, textAlign: 'center', flexShrink: 0 }}>🖥️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: selected ? 600 : 400, color: 'var(--label)' }}>
                    {h.hostname}
                  </div>
                  {!h.online && (
                    <div style={{ fontSize: 13, color: 'var(--label-secondary)', marginTop: 1 }}>Offline</div>
                  )}
                </div>
                {selected && (
                  <span style={{ color: 'var(--tint)', fontSize: 18, fontWeight: 700, flexShrink: 0 }}>✓</span>
                )}
              </button>
            )
          })}
        </div>
      ) : (
        <div style={{ margin: '8px 16px 0', padding: '10px 14px', background: 'var(--bg-grouped-cell)', borderRadius: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--label-tertiary)' }}>
            {boundHost
              ? boundHost.hostname
              : 'No host enrolled — file attachments unavailable'}
          </span>
        </div>
      )}

      {hostRemoved && (
        <div style={{ padding: '8px 16px 0', fontSize: 13, color: 'var(--label-secondary)' }}>
          This room's previous host was removed.{' '}
          {isOwner ? 'Pick a host above to restore attachments.' : 'Ask the room owner to pick a new host.'}
        </div>
      )}

      {wdPath && (
        <div style={{ margin: '8px 16px 0', padding: '10px 14px', background: 'var(--bg-grouped-cell)', borderRadius: 10 }}>
          <code style={{ fontSize: 12, color: 'var(--label-secondary)', wordBreak: 'break-all' }}>
            {wdPath}
          </code>
        </div>
      )}
    </div>
  )
}
